import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  InputAdornment
} from '@mui/material';
import {
  Person as PersonIcon,
  VpnKey as PasswordIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../hooks/useAuth';
import api from '../../utils/api';

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
      <div
          role="tabpanel"
          hidden={value !== index}
          id={`settings-tabpanel-${index}`}
          aria-labelledby={`settings-tab-${index}`}
          {...other}
      >
        {value === index && (
            <Box sx={{ p: 3 }}>
              {children}
            </Box>
        )}
      </div>
  );
}

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Profile settings state
  const [profileSettings, setProfileSettings] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    timezone: 'UTC+3'
  });

  // Load user data into form when component mounts or user changes
  useEffect(() => {
    if (user) {
      setProfileSettings({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        timezone: user.timezone || 'UTC+3'
      });
    }
  }, [user]);

  // Timezone options
  const timezones = [
    'UTC-12', 'UTC-11', 'UTC-10', 'UTC-9', 'UTC-8', 'UTC-7', 'UTC-6', 'UTC-5', 'UTC-4', 'UTC-3', 'UTC-2', 'UTC-1',
    'UTC',
    'UTC+1', 'UTC+2', 'UTC+3', 'UTC+4', 'UTC+5', 'UTC+6', 'UTC+7', 'UTC+8', 'UTC+9', 'UTC+10', 'UTC+11', 'UTC+12'
  ];

  // Password change state
  const [passwordSettings, setPasswordSettings] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Password validation errors
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Password visibility state
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setPasswordErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  // Handle profile settings change
  const handleProfileChange = (e) => {
    setProfileSettings({
      ...profileSettings,
      [e.target.name]: e.target.value
    });
  };

  // Handle password settings change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    setPasswordSettings({
      ...passwordSettings,
      [name]: value
    });

    // Clear specific field error when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors({
        ...passwordErrors,
        [name]: ''
      });
    }

    // Real-time validation
    validatePasswordField(name, value);
  };

  // Validate password fields
  const validatePasswordField = (name, value) => {
    let errorMessage = '';

    switch (name) {
      case 'newPassword':
        if (value && value.length < 8) {
          errorMessage = 'Password must be at least 8 characters long';
        }
        break;
      case 'confirmPassword':
        if (value && value !== passwordSettings.newPassword) {
          errorMessage = 'Passwords do not match';
        }
        break;
      default:
        break;
    }

    setPasswordErrors(prev => ({
      ...prev,
      [name]: errorMessage
    }));
  };

  // Toggle password visibility
  const handleTogglePasswordVisibility = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field]
    });
  };

  // Save profile settings
  const saveProfileSettings = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Saving profile settings:', profileSettings);

      const response = await api.put(`/users/${user.id}`, profileSettings);

      console.log('Profile update response:', response.data);

      // Обновляем пользователя в контексте авторизации
      if (response.data && response.data.user) {
        await updateUser(response.data.user);
        console.log('User updated in auth context');
      }

      setSuccess(true);
    } catch (err) {
      console.error('Error saving profile:', err);
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  // Change password
  const changePassword = async () => {
    setLoading(true);
    setError('');

    // Reset password errors
    setPasswordErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });

    try {
      // Validate passwords
      if (!passwordSettings.currentPassword) {
        setPasswordErrors(prev => ({ ...prev, currentPassword: 'Current password is required' }));
        return;
      }

      if (!passwordSettings.newPassword) {
        setPasswordErrors(prev => ({ ...prev, newPassword: 'New password is required' }));
        return;
      }

      if (passwordSettings.newPassword.length < 8) {
        setPasswordErrors(prev => ({ ...prev, newPassword: 'Password must be at least 8 characters long' }));
        return;
      }

      if (passwordSettings.newPassword !== passwordSettings.confirmPassword) {
        setPasswordErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
        return;
      }

      console.log('Changing password for user:', user.id);

      // Send password change request
      await api.put(`/users/${user.id}/password`, {
        currentPassword: passwordSettings.currentPassword,
        newPassword: passwordSettings.newPassword
      });

      // Clear password fields on success
      setPasswordSettings({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setSuccess(true);
    } catch (err) {
      console.error('Error changing password:', err);
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle API errors
  const handleApiError = (err) => {
    let errorMessage = 'An unexpected error occurred.';

    if (err.response) {
      // Сервер ответил с ошибкой
      if (err.response.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response.data?.errors && Array.isArray(err.response.data.errors)) {
        errorMessage = err.response.data.errors.map(e => e.msg || e.message).join(', ');
      } else {
        errorMessage = `Server error: ${err.response.status}`;
      }
    } else if (err.request) {
      // Нет ответа от сервера
      errorMessage = 'No response from server. Please check your internet connection.';
    } else if (err.message) {
      // Другая ошибка
      errorMessage = err.message;
    }

    setError(errorMessage);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSuccess(false);
  };

  // Check if profile form has changes
  const hasProfileChanges = () => {
    if (!user) return false;
    return (
        profileSettings.name !== (user.name || '') ||
        profileSettings.email !== (user.email || '') ||
        profileSettings.phone !== (user.phone || '') ||
        profileSettings.company !== (user.company || '') ||
        profileSettings.timezone !== (user.timezone || 'UTC+3')
    );
  };

  // Check if password form is valid
  const isPasswordFormValid = () => {
    return passwordSettings.currentPassword &&
        passwordSettings.newPassword &&
        passwordSettings.confirmPassword &&
        passwordSettings.newPassword === passwordSettings.confirmPassword &&
        passwordSettings.newPassword.length >= 8 &&
        !passwordErrors.currentPassword &&
        !passwordErrors.newPassword &&
        !passwordErrors.confirmPassword;
  };

  return (
      <DashboardLayout>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account settings and preferences.
          </Typography>
        </Box>

        {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError('')}>
              {error}
            </Alert>
        )}

        <Paper sx={{ mb: 4 }}>
          <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="settings tabs"
          >
            <Tab icon={<PersonIcon />} label="Profile" />
            <Tab icon={<PasswordIcon />} label="Change Password" />
          </Tabs>

          {/* Profile Settings */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={profileSettings.name}
                    onChange={handleProfileChange}
                    margin="normal"
                    disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={profileSettings.email}
                    onChange={handleProfileChange}
                    margin="normal"
                    disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={profileSettings.phone}
                    onChange={handleProfileChange}
                    margin="normal"
                    disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Company"
                    name="company"
                    value={profileSettings.company}
                    onChange={handleProfileChange}
                    margin="normal"
                    disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="timezone-label">Timezone</InputLabel>
                  <Select
                      labelId="timezone-label"
                      id="timezone"
                      name="timezone"
                      value={profileSettings.timezone}
                      label="Timezone"
                      onChange={handleProfileChange}
                      disabled={loading}
                  >
                    {timezones.map((tz) => (
                        <MenuItem key={tz} value={tz}>
                          {tz}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                  variant="contained"
                  onClick={saveProfileSettings}
                  disabled={loading || !hasProfileChanges()}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </TabPanel>

          {/* Password Settings */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Current Password"
                    name="currentPassword"
                    type={showPassword.currentPassword ? 'text' : 'password'}
                    value={passwordSettings.currentPassword}
                    onChange={handlePasswordChange}
                    margin="normal"
                    disabled={loading}
                    error={Boolean(passwordErrors.currentPassword)}
                    helperText={passwordErrors.currentPassword}
                    InputProps={{
                      endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                                onClick={() => handleTogglePasswordVisibility('currentPassword')}
                                edge="end"
                            >
                              {showPassword.currentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                      )
                    }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type={showPassword.newPassword ? 'text' : 'password'}
                    value={passwordSettings.newPassword}
                    onChange={handlePasswordChange}
                    margin="normal"
                    disabled={loading}
                    error={Boolean(passwordErrors.newPassword)}
                    helperText={passwordErrors.newPassword || 'Minimum 8 characters'}
                    InputProps={{
                      endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                                onClick={() => handleTogglePasswordVisibility('newPassword')}
                                edge="end"
                            >
                              {showPassword.newPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                      )
                    }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type={showPassword.confirmPassword ? 'text' : 'password'}
                    value={passwordSettings.confirmPassword}
                    onChange={handlePasswordChange}
                    margin="normal"
                    disabled={loading}
                    error={Boolean(passwordErrors.confirmPassword)}
                    helperText={passwordErrors.confirmPassword}
                    InputProps={{
                      endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                                onClick={() => handleTogglePasswordVisibility('confirmPassword')}
                                edge="end"
                            >
                              {showPassword.confirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                      )
                    }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                  variant="contained"
                  onClick={changePassword}
                  disabled={loading || !isPasswordFormValid()}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </Box>
          </TabPanel>
        </Paper>

        {/* Success Snackbar */}
        <Snackbar
            open={success}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity="success">
            Settings saved successfully!
          </Alert>
        </Snackbar>
      </DashboardLayout>
  );
};

export default Settings;